import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock, Mountain, Route, Users } from "lucide-react";
import { api } from "../lib/api";
import { MaskedLine, Reveal, Stagger, StaggerItem } from "../components/Motion";

export default function Hikes() {
  const [hikes, setHikes] = useState(null);
  useEffect(() => {
    document.title = "The Hikes — Salt & Stone, Hydra";
    api.get("/hikes").then((r) => setHikes(r.data)).catch(() => setHikes([]));
  }, []);

  return (
    <main data-testid="hikes-page" className="pt-28 sm:pt-36">
      <header className="mx-auto max-w-7xl px-5 sm:px-8">
        <MaskedLine>
          <span className="overline-label text-terra">Five routes · one island</span>
        </MaskedLine>
        <MaskedLine delay={0.12}>
          <h1 className="display-serif mt-4 text-5xl leading-[0.95] sm:text-6xl lg:text-7xl" data-testid="hikes-title">
            Every path earns its view.
          </h1>
        </MaskedLine>
        <Reveal delay={0.25}>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink/70 sm:text-lg">
            From a two-hour farmstead wander to the full-day lighthouse traverse — all private,
            all guided by Vasilis, all ending somewhere you'll want to stay a little longer.
          </p>
        </Reveal>
      </header>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28">
        {!hikes ? (
          <p className="text-ink/50" data-testid="hikes-loading">Loading the trails…</p>
        ) : (
          <Stagger className="flex flex-col gap-8">
            {hikes.map((hike, i) => (
              <StaggerItem key={hike.slug}>
                <Link
                  to={`/hikes/${hike.slug}`}
                  data-testid={`hike-row-${hike.slug}`}
                  className="group grid items-center gap-6 rounded-3xl border border-ink/10 bg-white p-4 transition-[box-shadow,border-color,transform] duration-500 hover:-translate-y-1 hover:border-terra/40 hover:shadow-[0_24px_60px_-32px_rgba(28,25,23,0.4)] sm:gap-8 sm:p-6 md:grid-cols-12"
                >
                  <div className={`relative md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <div className="aspect-[4/3] overflow-hidden rounded-2xl">
                      <img
                        src={hike.image}
                        alt={hike.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    </div>
                    <span className="absolute left-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-paper/90 text-sm font-semibold tracking-wider text-ink backdrop-blur" data-testid={`hike-num-${hike.slug}`}>
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className={`px-1 sm:px-3 md:col-span-5 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                    <h2 className="font-serif text-3xl tracking-tight sm:text-4xl">{hike.name}</h2>
                    <p className="mt-3 max-w-md text-base leading-relaxed text-ink/60">{hike.tagline}</p>
                    <div className="mt-6 grid max-w-md grid-cols-2 gap-x-3 gap-y-3 text-[11px] uppercase tracking-[0.12em] text-ink/60" data-testid={`hike-meta-${hike.slug}`}>
                      <span className="flex items-center gap-2 rounded-full bg-olive-pale px-4 py-2"><Mountain size={13} className="shrink-0 text-terra" /> {hike.difficulty}</span>
                      <span className="flex items-center gap-2 rounded-full bg-olive-pale px-4 py-2"><Clock size={13} className="shrink-0 text-terra" /> {hike.duration}</span>
                      <span className="flex items-center gap-2 rounded-full bg-olive-pale px-4 py-2"><Route size={13} className="shrink-0 text-terra" /> {hike.distance}</span>
                      <span className="flex items-center gap-2 rounded-full bg-olive-pale px-4 py-2"><Users size={13} className="shrink-0 text-terra" /> {hike.group_size}</span>
                    </div>
                  </div>
                  <div className={`flex items-center justify-between gap-4 px-1 pb-1 sm:px-3 md:col-span-2 md:flex-col md:items-end md:justify-center md:gap-5 md:pb-0 ${i % 2 === 1 ? "md:order-3" : ""}`}>
                    <span className="text-lg font-medium text-terra">{hike.price}</span>
                    <span className="flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm uppercase tracking-[0.18em] text-ink/70 transition-[background-color,color,border-color] duration-300 group-hover:border-terra group-hover:bg-terra group-hover:text-paper">
                      View hike <ArrowUpRight size={14} />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        )}
      </section>
    </main>
  );
}
